import React from 'react';

// Skeleton animation style
const skeletonAnimation = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite'
};

// Generic skeleton component
interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  circle?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({ 
  className = '', 
  width = '100%', 
  height = '20px',
  circle = false 
}) => {
  return (
    <div
      className={`animate-pulse bg-gray-200 dark:bg-gray-700 ${circle ? 'rounded-full' : 'rounded'} ${className}`}
      style={{ 
        width, 
        height,
        ...skeletonAnimation
      }}
    />
  );
};

// Whiteboard skeleton
export const WhiteboardSkeleton: React.FC = () => {
  return (
    <div className="w-full h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Toolbar skeleton */}
      <div className="h-20 bg-white dark:bg-gray-800 shadow-md px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Skeleton width={40} height={40} circle />
          <Skeleton width={120} height={24} />
        </div>
        <div className="flex items-center space-x-2">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} width={40} height={40} className="rounded-lg" />
          ))}
        </div>
      </div>

      {/* Canvas area skeleton */}
      <div className="flex-1 p-4">
        <div className="w-full h-full bg-white dark:bg-gray-800 rounded-lg shadow-lg flex items-center justify-center">
          <div className="text-center">
            <Skeleton width={200} height={200} className="mx-auto mb-4" />
            <Skeleton width={150} height={20} className="mx-auto mb-2" />
            <Skeleton width={200} height={16} className="mx-auto" />
          </div>
        </div>
      </div>

      {/* Question navigation skeleton */}
      <div className="h-16 bg-white dark:bg-gray-800 shadow-md px-4 py-2 flex items-center justify-between">
        <Skeleton width={40} height={40} className="rounded-lg" />
        <div className="flex items-center space-x-1">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} width={40} height={40} className="rounded-lg" />
          ))}
        </div>
        <Skeleton width={40} height={40} className="rounded-lg" />
      </div>
    </div>
  );
};

// Question display skeleton
export const QuestionDisplaySkeleton: React.FC = () => {
  return (
    <div className="p-6 space-y-4">
      <Skeleton height={24} width="60%" />
      <div className="space-y-2">
        <Skeleton height={16} />
        <Skeleton height={16} />
        <Skeleton height={16} width="80%" />
      </div>
      <div className="space-y-3 mt-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="flex items-center space-x-3">
            <Skeleton width={20} height={20} circle />
            <Skeleton height={16} width="70%" />
          </div>
        ))}
      </div>
    </div>
  );
};

// Test list skeleton
export const TestListSkeleton: React.FC = () => {
  return (
    <></>
    // <div className="space-y-4">
    //   {[1, 2, 3, 4].map(i => (
    //     <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
    //       <div className="flex items-center justify-between">
    //         <div className="flex-1">
    //           <Skeleton height={20} width="60%" className="mb-2" />
    //           <Skeleton height={16} width="40%" />
    //         </div>
    //         <Skeleton width={80} height={32} className="rounded-md" />
    //       </div>
    //     </div>
    //   ))}
    // </div>
  );
};

// Loading spinner component
interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large';
  color?: string;
  text?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'medium', 
  color = 'blue',
  text 
}) => {
  const sizeClasses = {
    small: 'w-6 h-6 border-2',
    medium: 'w-10 h-10 border-3',
    large: 'w-16 h-16 border-4'
  };

  return (
    <div className="flex flex-col items-center justify-center">
      <div 
        className={`
          ${sizeClasses[size]} 
          border-${color}-600 
          border-t-transparent 
          rounded-full 
          animate-spin
        `}
      />
      {text && (
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">{text}</p>
      )}
    </div>
  );
};

// Full page loader
export const FullPageLoader: React.FC<{ text?: string }> = ({ text = 'Yükleniyor...' }) => {
  return (
    <div className="fixed inset-0 bg-white dark:bg-gray-900 flex items-center justify-center z-50">
      <div className="text-center">
        <LoadingSpinner size="large" text={text} />
      </div>
    </div>
  );
};

// Content loader with error and retry
interface ContentLoaderProps {
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
  skeleton?: React.ReactNode;
  children: React.ReactNode;
}

export const ContentLoader: React.FC<ContentLoaderProps> = ({ 
  isLoading, 
  error, 
  onRetry,
  skeleton,
  children 
}) => {
  if (isLoading) {
    return <>{skeleton || <LoadingSpinner />}</>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="text-red-500 mb-4">
          <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
            />
          </svg>
        </div>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {error.message || 'Bir hata oluştu'}
        </p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Tekrar Dene
          </button>
        )}
      </div>
    );
  }

  return <>{children}</>;
};

// Add shimmer animation to global styles
const shimmerStyles = `
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = shimmerStyles;
  document.head.appendChild(style);
}

// Main LoadingStates component
export const LoadingStates: React.FC<{ type?: string }> = ({ type }) => {
  switch (type) {
    case 'test':
      return <TestListSkeleton />;
    case 'question':
      return <QuestionDisplaySkeleton />;
    case 'whiteboard':
      return <WhiteboardSkeleton />;
    default:
      return <FullPageLoader />;
  }
};
